import { validationMixin } from "vuelidate";
import {
  required,
  numeric,
  minLength,
  maxLength,
  helpers,
  email
} from "vuelidate/lib/validators";
import { API, graphqlOperation } from "aws-amplify";
import AppSettings from "@/appsettings/appsettings.js";

const DEFAULT_CVC_LENGTH = 3;

const creditCardNumberValid = getlocalCardTypeValidation => (value, vm) => {
  try {
    const selectedCardType = vm.cardType;
    if (!selectedCardType || !selectedCardType.value) return false;
    const cardType = getlocalCardTypeValidation(selectedCardType.value);
    if (!cardType.shortCardName) return false;

    const sanitizedNumber = value.replace(/\s/g, "");

    if (sanitizedNumber.length < cardType.constraints.cardLength) return false;

    const startingDigits = cardType.startingDigits;
    const cardNumberSubstring = sanitizedNumber.substring(
      0,
      startingDigits.length
    );

    return cardNumberSubstring === startingDigits;
  } catch (e) {}
  return false;
};

const cvcValidation = getlocalCardTypeValidation => (value, vm) => {
  try {
    const selectedCardType = vm.cardType;
    if (!selectedCardType || !selectedCardType.value) return false;
    const cardType = getlocalCardTypeValidation(selectedCardType.value);
    if (!cardType.shortCardName) return false;

    if (
      value.length < (cardType?.constraints?.cvcMinLength ?? DEFAULT_CVC_LENGTH)
    )
      return false;
    if (
      value.length > (cardType?.constraints?.cvcMaxLength ?? DEFAULT_CVC_LENGTH)
    )
      return false;

    return true;
  } catch (e) {}

  return false;
};

const expiryDateValidation = value => {
  if (value === "") {
    return false;
  }
  var values = value.split(" / ");

  var date = new Date();
  if (
    values[1] <
    date
      .getFullYear()
      .toString()
      .substr(2, 4)
  ) {
    return false;
  }

  if (
    values[1] ===
    date
      .getFullYear()
      .toString()
      .substr(2, 4)
  ) {
    if (values[0] < date.getMonth() + 1) {
      return false;
    }
  }

  return true;
};

const MINIMUMORDERAMOUNT = 0;

const parseToFloat = value => parseFloat(value.toString().replace(",", ""));

const isFloat = value => !isNaN(parseToFloat(value));

const orderAmountNoLessThan = min => value => parseToFloat(value) > min;

const numericFilterRegex = /\D/g;

const facStringRegex = /^[a-zA-Z0-9-_., ]+$/;

const facStringFilterRegex = /[^a-zA-Z0-9-_., ]/g;

const facStrictStringRegex = /^[a-zA-Z0-9]+$/;

const facStrictStringFilterRegex = /[^a-zA-Z0-9]/g;

const facStringValidation = helpers.regex(
  "facStringValidation",
  facStringRegex
);

const facStrictStringValidation = helpers.regex(
  "facStrictStringValidation",
  facStrictStringRegex
);

export default {
  parseToFloat,
  mixins: [validationMixin],
  data() {
    return {
      cardTypeObject: {
        shortCardName: "",
        cardName: "",
        startingDigits: "",
        constraints: {
          cardLength: 16,
          cvcMinLength: DEFAULT_CVC_LENGTH,
          cvcMaxLength: DEFAULT_CVC_LENGTH
        }
      },
      cardValidationMap: {
        defaultCard: {
          shortCardName: "",
          cardName: "",
          startingDigits: "",
          constraints: {
            cardLength: 16,
            cvcMinLength: DEFAULT_CVC_LENGTH,
            cvcMaxLength: DEFAULT_CVC_LENGTH
          }
        }
      },
      billingInfoFilter: {
        FirstName: {
          filterRegex: facStringFilterRegex,
          maxLength: 30
        },
        LastName: {
          filterRegex: facStringFilterRegex,
          maxLength: 30
        },
        Address1: {
          filterRegex: facStringFilterRegex,
          maxLength: 50
        },
        Address2: {
          filterRegex: facStringFilterRegex,
          maxLength: 50
        },
        City: {
          filterRegex: facStringFilterRegex,
          maxLength: 30
        },
        State: {
          filterRegex: facStrictStringFilterRegex,
          minLength: 2,
          maxLength: 5
        },
        ZipCode: {
          filterRegex: facStrictStringFilterRegex,
          maxLength: 10
        },
        Country: {
          filterRegex: numericFilterRegex,
          minLength: 3,
          maxLength: 3
        }
      }
    };
  },
  validations: {
    form: {
      customFieldsModel: {},
      orderInformation: {
        firstName: {
          required
        },
        lastName: {
          required
        },
        email: {
          required,
          email
        },
        orderNumber: {
          required
        }
      },
      orderDetails: {
        amount: {
          required,
          isFloat,
          orderAmountNoLessThan: orderAmountNoLessThan(MINIMUMORDERAMOUNT)
        },
        currency: {
          required
        }
      },
      billingInfo: {
        FirstName: {
          required,
          facStringValidation,
          maxLength(value) {
            return maxLength(this.billingInfoFilter.FirstName.maxLength)(value);
          }
        },
        LastName: {
          required,
          facStringValidation,
          maxLength(value) {
            return maxLength(this.billingInfoFilter.LastName.maxLength)(value);
          }
        },
        Address1: {
          required,
          facStringValidation,
          maxLength(value) {
            return maxLength(this.billingInfoFilter.Address1.maxLength)(value);
          }
        },
        Address2: {
          facStringValidation,
          maxLength(value) {
            return maxLength(this.billingInfoFilter.Address2.maxLength)(value);
          }
        },
        City: {
          required,
          facStringValidation,
          maxLength(value) {
            return maxLength(this.billingInfoFilter.City.maxLength)(value);
          }
        },
        State: {
          facStrictStringValidation,
          minLength(value) {
            return minLength(this.billingInfoFilter.State.minLength)(value);
          },
          maxLength(value) {
            return maxLength(this.billingInfoFilter.State.maxLength)(value);
          }
        },
        ZipCode: {
          required,
          facStrictStringValidation,
          maxLength(value) {
            return maxLength(this.billingInfoFilter.ZipCode.maxLength)(value);
          }
        },
        Country: {
          required,
          numeric,
          minLength(value) {
            return minLength(this.billingInfoFilter.State.minLength)(value);
          },
          maxLength(value) {
            return maxLength(this.billingInfoFilter.State.maxLength)(value);
          }
        }
      },
      creditCardInfo: {
        cnumber: {
          required,
          creditCardNumberValid(value, vm) {
            return creditCardNumberValid(this.getlocalCardTypeValidation)(
              value,
              vm
            );
          }
        },
        cardHolderName: {
          required
        },
        cvc: {
          required,
          cvcValidation(value, vm) {
            return cvcValidation(this.getlocalCardTypeValidation)(value, vm);
          },
          numeric
        },
        expiryDate: {
          required,
          expiryDateValidation,
          minLength: minLength(7),
          maxLength: maxLength(7)
        },
        cardType: {
          required
        }
      }
    }
  },
  computed: {
    isAuthenticated() {
      return this.$store.getters.isAuthenticated;
    },
    authMode() {
      return this.isAuthenticated ? null : AppSettings.UnauthenticatedAuthMode;
    },
    validationMethods() {
      return [
        this.requiredMessage,
        this.numericMessage,
        this.orderAmountNoLessThanMessage,
        this.minLengthMessage,
        this.maxLengthMessage,
        this.creditCardNumberValidMessage,
        this.cvcValidationMessage,
        this.creditCardSupportedMessage,
        this.expiryDateValidationMessage,
        this.isFloatMessage,
        this.facStringValidationMessage,
        this.facStrictStringValidationMessage,
        this.emailValidationMessage
      ];
    },
    creditCardLength() {
      return this.cardTypeObject.constraints.cardLength;
    },
    cvcMaxLength() {
      return (
        this.cardTypeObject?.constraints?.cvcMaxLength ?? DEFAULT_CVC_LENGTH
      );
    },
    errorMessages() {
      return {
        orderInformation: this.orderInformationErrors,
        billingInfo: this.billingInfoErrors,
        creditCardInfo: this.creditCardInfoErrors,
        orderDetails: this.orderDetailsErrors
      };
    },
    orderInformationErrors() {
      let parent = "orderInformation";

      return {
        firstName: this.generateErrorMessage(parent, "firstName"),
        lastName: this.generateErrorMessage(parent, "lastName"),
        email: this.generateErrorMessage(parent, "email"),
        orderNumber: this.generateErrorMessage(parent, "orderNumber")
      };
    },
    orderDetailsErrors() {
      let parent = "orderDetails";

      return {
        amount: this.generateErrorMessage(parent, "amount"),
        currency: this.generateErrorMessage(parent, "currency")
      };
    },
    billingInfoErrors() {
      let parent = "billingInfo";
      return {
        FirstName: this.generateErrorMessage(parent, "FirstName"),
        LastName: this.generateErrorMessage(parent, "LastName"),
        Address1: this.generateErrorMessage(parent, "Address1"),
        Address2: this.generateErrorMessage(parent, "Address2"),
        City: this.generateErrorMessage(parent, "City"),
        State: this.generateErrorMessage(parent, "State"),
        ZipCode: this.generateErrorMessage(parent, "ZipCode"),
        Country: this.generateErrorMessage(parent, "Country")
      };
    },
    creditCardInfoErrors() {
      let parent = "creditCardInfo";
      return {
        cnumber: this.generateErrorMessage(parent, "cnumber"),
        cardHolderName: this.generateErrorMessage(parent, "cardHolderName"),
        cvc: this.generateErrorMessage(parent, "cvc"),
        expiryDate: this.generateErrorMessage(parent, "expiryDate"),
        cardType: this.generateErrorMessage(parent, "cardType")
      };
    }
  },
  watch: {
    selectedCardType(newValue) {
      if (newValue && newValue.value) {
        this.getCardTypeValidationInformation(newValue.value).then(vObject => {
          this.cardTypeObject = vObject;
          const originalCnumber = this.form.creditCardInfo.cnumber;
          this.form.creditCardInfo.cnumber = "";
          this.form.creditCardInfo.cnumber = originalCnumber;
          this.$v.form.creditCardInfo.cnumber.$touch();

          const originalCvc = this.form.creditCardInfo.cvc;
          this.form.creditCardInfo.cvc = "";
          this.form.creditCardInfo.cvc = originalCvc;
          this.$v.form.creditCardInfo.cvc.$touch();
        });
      }
    }
  },
  methods: {
    async retrieveCardValidationFromBackend(cardType) {
      const getCardTypeByShortName = `
      query GetCardTypeByShortName($shortCardName: String!) {
        getCardTypeByShortName(shortCardName: $shortCardName) {
            cardType{
              cardName
              constraints
              shortCardName
              startingDigits
            }
            statusMessage
          }
      }
    `;

      const getCardTypeByShortNameResponse = await API.graphql({
        ...graphqlOperation(getCardTypeByShortName, {
          shortCardName: cardType
        }),
        authMode: this.authMode
      });

      const validationInfo =
        getCardTypeByShortNameResponse.data.getCardTypeByShortName.cardType;
      if (!validationInfo) {
        const statusMessage =
          getCardTypeByShortNameResponse.data.getCardTypeByShortName
            .statusMessage;
        throw new Error(statusMessage);
      }

      validationInfo.constraints = JSON.parse(validationInfo.constraints);

      return validationInfo;
    },
    async getCardTypeValidationInformation(cardType) {
      try {
        if (!cardType) return this.cardValidationMap.defaultCard;

        const localCardType = this.getlocalCardTypeValidation(cardType);
        if (!!localCardType.shortCardName) return localCardType;
        const backEndCardType = await this.retrieveCardValidationFromBackend(
          cardType
        );
        if (!!backEndCardType) {
          this.cardValidationMap[cardType] = backEndCardType;
          return backEndCardType;
        }
      } catch (e) {
        this.$logger.error("Error getting card validation for ", cardType);
        this.$logger.error(e);
      }
      return this.cardValidationMap.defaultCard;
    },
    getlocalCardTypeValidation(cardType) {
      const localCardType = this.cardValidationMap[cardType];
      if (!localCardType) return this.cardValidationMap.defaultCard;
      return localCardType;
    },
    async retrieveCardTypeValidationFromList(cardTypes) {
      const vm = this;
      const cardTypeValidationPromises = cardTypes.map(async c => {
        await vm.getCardTypeValidationInformation(c.value);
      });

      await Promise.all(cardTypeValidationPromises);
    },
    generateErrorMessage(parent, name) {
      let validationInfo = this.$v.form[parent][name];
      if (!validationInfo.$dirty) return null;

      if (!validationInfo.$error) return "";

      let message = this.validationMethods.reduce(
        (errorMessage, validationMethod) =>
          errorMessage + validationMethod(validationInfo),
        ""
      );

      return message;
    },
    requiredMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("required")) return "";

      let message = "This is a required field. ";

      return validationInfo.required ? "" : message;
    },
    numericMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("numeric")) return "";

      let message = "This field only allows numeric values. ";

      return validationInfo.numeric ? "" : message;
    },
    orderAmountNoLessThanMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("orderAmountNoLessThan")) return "";

      let message = `The order amount must be greater than ${MINIMUMORDERAMOUNT}`;

      return validationInfo.orderAmountNoLessThan ? "" : message;
    },
    minLengthMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("minLength")) return "";

      let min = validationInfo.$params.minLength.min;

      let message = `This field must have atleast ${min} characters. `;

      return validationInfo.minLength ? "" : message;
    },
    maxLengthMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("maxLength")) return "";

      let max = validationInfo.$params.maxLength.max;

      let message = `This field must have no more than ${max} characters. `;

      return validationInfo.maxLength ? "" : message;
    },
    creditCardNumberValidMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("creditCardNumberValid")) return "";

      let message = "The credit card number entered is invalid. ";

      return validationInfo.creditCardNumberValid ? "" : message;
    },
    cvcValidationMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("cvcValidation")) return "";

      let message = "The cvc number entered is invalid. ";

      return validationInfo.cvcValidation ? "" : message;
    },
    creditCardSupportedMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("creditCardSupported")) return "";

      let message = "This card type is not supported. ";

      return validationInfo.creditCardSupported ? "" : message;
    },
    expiryDateValidationMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("expiryDateValidation")) return "";

      let message = "This cannot be a date in the past. ";

      return validationInfo.expiryDateValidation ? "" : message;
    },
    isFloatMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("isFloat")) return "";

      let message = "The amount entered is invalid. ";

      return validationInfo.isFloat ? "" : message;
    },
    facStringValidationMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("facStringValidation")) return "";

      let message =
        "This field contains an invalid character. Valid characters: a-z A-Z 0-9 - _ . , [space]. ";

      return validationInfo.isFloat ? "" : message;
    },
    facStrictStringValidationMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("facStrictStringValidation"))
        return "";

      let message =
        "This field contains an invalid character. Valid characters: a-z A-Z 0-9";

      return validationInfo.isFloat ? "" : message;
    },
    emailValidationMessage(validationInfo) {
      if (!validationInfo.hasOwnProperty("email")) return "";

      let message = "The email address entered is invalid. ";

      return validationInfo.email ? "" : message;
    }
  }
};
